<template>
  <v-row>
    <v-col class="fw-bold">{{i18n.global.t('pages.firmwareBulkUpdate.removeDeviceSearch.name')}}</v-col>
  </v-row>
  <v-row>
    <v-col class="d-flex align-items-center">
      <SingleOperationOfficeAutoComplete
        v-model="selectedOperationOffice"
        @update:modelValue="searchDeviceHandler"
      />
      <DeviceIdAutoComplete
        class="ml-4 mr-4"
        v-model="selectedDevice"
        :operationOffices="selectedOperationOffice ? [selectedOperationOffice]:[]"
        @update:modelValue="searchDeviceHandler"
      />
      <VehicleAutoComplete
        v-model="selectedVehicle"
        :operationOffices="selectedOperationOffice ? [selectedOperationOffice]:[]"
        :selectedDepartment="null"
        @update:modelValue="searchDeviceHandler"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col class="fw-bold">{{ i18n.global.t('pages.firmwareBulkUpdate.removeDeviceSearch.result') }}</v-col>
  </v-row>
  <v-row>
    <v-col>
      <RemoveDeviceTable
        v-model="deviceList"
        :isShowTable="isSearched"
      ></RemoveDeviceTable>
    </v-col>
  </v-row>

  <v-row>
    <v-col class="d-flex justify-content-end align-items-center">
      <v-pagination
        v-model="currentPage"
        :length="totalPage"
        :total-visible="6"
        density="compact"
        @click="searchDevice()"
      ></v-pagination>
    </v-col>
  </v-row>

  <v-divider class="my-4"></v-divider>

  <v-row>
    <v-col class="d-flex justify-content-center align-items-center">
      <v-btn
        class="mr-2"
        @click="moveToSearchedDeviceList"
      >▲</v-btn>
      <v-btn
        class="ml-2"
        @click="moveToRemovedDeviceList"
      >▼</v-btn>
    </v-col>
  </v-row>
  <v-row>
    <v-col class="fw-bold">{{ i18n.global.t('pages.firmwareBulkUpdate.removeDeviceSearch.removeDevice') }}</v-col>
  </v-row>
  <v-row>
    <v-col>
      <RemoveDeviceTable
        v-model="removedDeviceList"
        :isShowTable="true"
        :errorMessages="props.errorMessages"
      ></RemoveDeviceTable>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Api from '@/services/api/ApiServiceFabrick';
import i18n from '@/lang/i18n';
import VehicleAutoComplete from '@/components/AutoCompletes/VehicleAutoComplete.vue';
import DeviceIdAutoComplete from '@/components/AutoCompletes/DeviceIdAutoComplete.vue';
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue';
import RemoveDeviceTable from '@/components/Firmware/BulkUpdate/RemoveDeviceTable.vue';
import getOperationOffice from '@/types/responses/getOperationOffice';
import getVehicleResponse from '@/types/responses/getVehicle';
import getPaginatedDevicesRequest from '@/types/requests/getPaginatedDevices'
import getDeviceResponse from '@/types/responses/getDevice';
import FirmwareRemoveDeviceType from '@/types/FirmwareRemoveDeviceType';
import getDevice from '@/types/responses/getDevice';

const emit = defineEmits(['update:modelValue']);
const props = defineProps<{
  modelValue: number[]
  removeDevices?: getDevice[]
  errorMessages?: string[];
}>()

watch(
  () => props.removeDevices,
  () => {
    if (props.removeDevices) {
      removedDeviceList.value = props.removeDevices.reduce<FirmwareRemoveDeviceType[]>((acc, device) => {
          const newDevice: FirmwareRemoveDeviceType = {
            ...device,
            isChecked: false
          }
          acc.push(newDevice)

        return acc;
      }, []);
    }
  }
)

const removedDeviceIds = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
})

const selectedOperationOffice = ref<getOperationOffice>()
const selectedDevice = ref<getDeviceResponse | undefined>()
const selectedVehicle = ref<getVehicleResponse | undefined>()
const deviceList = ref<FirmwareRemoveDeviceType[]>([])
const removedDeviceList = ref<FirmwareRemoveDeviceType[]>([])
const isSearched = ref(true)
const currentPage = ref<number>(1)
const totalPage = ref<number>(0)

function searchDeviceHandler() {
  currentPage.value = 1
  searchDevice()
}

function searchDevice() {
  isSearched.value = false
  const params = {
    page: currentPage.value
  } as getPaginatedDevicesRequest

  if (selectedOperationOffice.value) {
    const operataionOfficeId = selectedOperationOffice.value.id;
    params.operation_office_id = operataionOfficeId
  }

  if (selectedDevice.value) {
    const deviceId = selectedDevice.value.device_id;
    params.device_id = deviceId
  }

  if (selectedVehicle.value) {
    const vehicleId = selectedVehicle.value.id;
    params.vehicle_id = vehicleId
  }

  Api().getPaginatedDevices(params).then((data) => {
    deviceList.value = data.devices.reduce<FirmwareRemoveDeviceType[]>((acc, device) => {
      if (!removedDeviceIds.value.includes(device.id)) {
        const newDevice: FirmwareRemoveDeviceType = {
          ...device,
          isChecked: false
        }
        acc.push(newDevice)
      }

      return acc;
    }, []);
    currentPage.value = data.current_page
    totalPage.value = data.total_page
    isSearched.value = true
  }).catch((error) => {
    const res = error.response
    console.log(res)
  })
}

function moveToSearchedDeviceList() {
  const movedList = [] as FirmwareRemoveDeviceType[]
  removedDeviceList.value?.forEach((device) => {
    if (device.isChecked) {
      device.isChecked = false
      deviceList.value.push(device)
    } else {
      movedList.push(device)
    }
  })
  removedDeviceList.value = movedList
  setRemovedDeviceIds()
}
function moveToRemovedDeviceList() {
  const movedList = [] as FirmwareRemoveDeviceType[]
  deviceList.value?.forEach((device) => {
    if (device.isChecked) {
      device.isChecked = false
      removedDeviceList.value.push(device)
    } else {
      movedList.push(device)
    }
  })
  setRemovedDeviceIds()
  deviceList.value = movedList
}

function setRemovedDeviceIds() {
  removedDeviceIds.value = removedDeviceList.value.map((device) => {
    return device.id
  })
}
</script>
